import type { InstallLaborLine, ProposalNodeSet, TravelNodeData } from '../types';
import {
  getInstallLaborLines,
  isBlankLaborLine,
  laborLineDayTotal,
  laborLineEffectiveQuantity,
  laborLineJobTotal,
} from './proposalInstallLabor';

export type TravelRateInputs = {
  mileageRate: number;
  travelDayRate: number;
};

export type TravelLaborLine = Pick<
  InstallLaborLine,
  'id' | 'role' | 'quantity' | 'ratePerDay' | 'employeeUserId'
>;

export function travelMileageTotal(
  travel: Pick<TravelNodeData, 'milesToJobSite'>,
  rates: Pick<TravelRateInputs, 'mileageRate'>,
): number {
  return (Number(travel.milesToJobSite) || 0) * 2 * (Number(rates.mileageRate) || 0);
}

export const DEFAULT_HOTEL_ROOMS = 2;

export function travelHotelRooms(travel: Pick<TravelNodeData, 'hotelRooms'>): number {
  if (travel.hotelRooms == null) return DEFAULT_HOTEL_ROOMS;
  const rooms = Number(travel.hotelRooms);
  if (!Number.isFinite(rooms)) return DEFAULT_HOTEL_ROOMS;
  return Math.max(0, rooms);
}

export function travelHotelTotal(
  travel: Pick<TravelNodeData, 'hotelNights' | 'hotelRatePerNight' | 'hotelRooms'>,
): number {
  return (
    travelHotelRooms(travel) *
    (Number(travel.hotelRatePerNight) || 0) *
    (Number(travel.hotelNights) || 0)
  );
}

/** Nights on site: (install days + travel days) − 1. */
export function defaultHotelNights(installDays: number, travelDays: number): number {
  return Math.max(0, (Number(installDays) || 0) + (Number(travelDays) || 0) - 1);
}

export function isHotelNightsAutocalculated(
  hotelNights: number,
  installDays: number,
  travelDays: number,
): boolean {
  const current = Number(hotelNights) || 0;
  const auto = defaultHotelNights(installDays, travelDays);
  return current === 0 || current === auto;
}

/** Keep hotel nights on the default formula until the user edits them. */
export function withDefaultHotelNights<T extends { hotelNights: number; travelDays: number }>(
  travel: T,
  installDays: number,
  previousInstallDays: number,
  previousTravelDays: number,
): T {
  const next = defaultHotelNights(installDays, travel.travelDays);
  const previous = defaultHotelNights(previousInstallDays, previousTravelDays);
  const previousLegacy = Math.max(0, Number(previousInstallDays) || 0);
  const current = Number(travel.hotelNights) || 0;
  if (current === 0 || current === previous || current === previousLegacy) {
    return { ...travel, hotelNights: next };
  }
  return travel;
}

export function travelPerDiemPersonQuantity(
  line: TravelLaborLine,
  travel: Pick<TravelNodeData, 'crewSize'>,
): number {
  if (line.id === 'legacy-crew') return Number(travel.crewSize) || 0;
  return travelPersonQuantity(line);
}

export function travelPerDiemHeadcount(
  travel: Pick<TravelNodeData, 'crewSize'>,
  laborLines?: TravelLaborLine[],
): number {
  const people = listTravelDayPeople(laborLines);
  if (people.length > 0) {
    return people.reduce((sum, line) => sum + travelPerDiemPersonQuantity(line, travel), 0);
  }
  return Number(travel.crewSize) || 0;
}

export function travelPerDiemPersonTotal(
  line: TravelLaborLine,
  travel: Pick<TravelNodeData, 'hotelNights' | 'perDiemPerPersonPerDay' | 'crewSize'>,
): number {
  const nights = Number(travel.hotelNights) || 0;
  const rate = Number(travel.perDiemPerPersonPerDay) || 0;
  return travelPerDiemPersonQuantity(line, travel) * rate * nights;
}

export function travelPerDiemTotal(
  travel: Pick<TravelNodeData, 'hotelNights' | 'crewSize' | 'perDiemPerPersonPerDay'>,
  laborLines?: TravelLaborLine[],
): number {
  const nights = Number(travel.hotelNights) || 0;
  const rate = Number(travel.perDiemPerPersonPerDay) || 0;
  return travelPerDiemHeadcount(travel, laborLines) * rate * nights;
}

export function listTravelDayPeople(lines: TravelLaborLine[] | undefined): TravelLaborLine[] {
  return (lines || []).filter((line) => !isBlankLaborLine(line as InstallLaborLine));
}

/** Daily travel-day cost: listed labor people × their $/day, else the settings travel day rate. */
export function travelDayPeopleRate(
  rates: Pick<TravelRateInputs, 'travelDayRate'>,
  laborLines?: TravelLaborLine[],
): number {
  const people = listTravelDayPeople(laborLines);
  if (people.length > 0) {
    return people.reduce((sum, line) => sum + laborLineDayTotal(line), 0);
  }
  return Number(rates.travelDayRate) || 0;
}

export function travelDaysTotal(
  travel: Pick<TravelNodeData, 'travelDays'>,
  rates: Pick<TravelRateInputs, 'travelDayRate'>,
  laborLines?: TravelLaborLine[],
): number {
  const days = Number(travel.travelDays) || 0;
  const people = listTravelDayPeople(laborLines);
  if (people.length > 0) {
    return people.reduce((sum, line) => sum + laborLineJobTotal(line, days), 0);
  }
  return days * (Number(rates.travelDayRate) || 0);
}

export function travelPersonDayTotal(
  line: TravelLaborLine,
  travelDays: number,
): number {
  return laborLineJobTotal(line, travelDays);
}

export function travelPersonQuantity(line: TravelLaborLine): number {
  return laborLineEffectiveQuantity(line);
}

/** Travel raw total. Fuel here is travel.fuelBudget, not install.fuelBudget. */
export function computeTravelNodeRawTotal(
  travel: TravelNodeData,
  rates: TravelRateInputs,
  laborLines?: TravelLaborLine[],
): number {
  return (
    travelMileageTotal(travel, rates) +
    travelHotelTotal(travel) +
    travelPerDiemTotal(travel, laborLines) +
    (Number(travel.fuelBudget) || 0) +
    travelDaysTotal(travel, rates, laborLines)
  );
}

const TRAVEL_TOTAL_DRIFT_EPS = 0.01;

/** Keep persisted travel.total aligned with inputs (fuel, mileage, hotel, etc.). */
export function stampTravelNodeTotal(
  travel: TravelNodeData,
  rates: TravelRateInputs,
  laborLines?: TravelLaborLine[],
): TravelNodeData {
  const total = computeTravelNodeRawTotal(travel, rates, laborLines);
  const stored = Number(travel.total) || 0;
  if (Math.abs(stored - total) < TRAVEL_TOTAL_DRIFT_EPS) return travel;
  return { ...travel, total };
}

export function normalizeTravelTotalOnLoad(
  nodeSet: ProposalNodeSet,
  rates: TravelRateInputs,
): ProposalNodeSet {
  const laborLines = getInstallLaborLines(nodeSet.install);
  const travel = stampTravelNodeTotal(nodeSet.travel, rates, laborLines);
  if (travel === nodeSet.travel) return nodeSet;
  return { ...nodeSet, travel };
}
